// Dependencies
import styled, { css } from 'styled-components';

// Components
import { Typography } from '../../display/Typography';
import { ArrowBottom } from '../../../assets/icons/ArrowBottom';

// Styles
import { hoverCapable } from '../../../styles/hover';

// Types
import { InputSizeTypes, TypeDropDown } from './types';

type ContainerProps = {
  typeDropDown: TypeDropDown;
  labelText: boolean;
  inputSize: InputSizeTypes;
};

type HeaderProps = {
  typeDropDown: TypeDropDown;
  isActive: boolean;
  borderError?: boolean;
  isDisabled?: boolean;
};

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs};
  cursor: pointer;
  width: 100%;
  ${({ inputSize }) =>
    ({
      XXL: css`
        width: 38rem;
      `,
      XL: css`
        width: 35rem;
      `,
      L: css`
        width: 27rem;
      `,
      M: css`
        width: 22rem;
      `,
      S: css`
        width: 17rem;
      `,
      XS: css`
        width: 10rem;
      `,
    })[inputSize]};

  @media (max-width: ${({ theme }) => theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    ${({ inputSize }) =>
      ({
        XXL: css`
          width: 100%;
          max-width: 38rem;
        `,
        XL: css`
          width: 100%;
          max-width: 35rem;
        `,
        L: css`
          width: 100%;
          max-width: 27rem;
        `,
        M: css`
          width: 100%;
          max-width: 22rem;
        `,
        S: css`
          width: 100%;
          max-width: 17rem;
        `,
        XS: css`
          width: 100%;
          max-width: 10rem;
        `,
      })[inputSize]};
  }
`;

export const Label = styled(Typography)`
  white-space: nowrap;
  font-size: ${({ theme }) => theme.fontSize.S};
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.XS};
  }
`;

export const DropDownContainer = styled.div`
  position: relative;
`;

export const DropDownHeader = styled.div<HeaderProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxxs};
  padding: 0 0.8rem 0 1rem;
  height: 4rem;
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.black};
  overflow: hidden;

  ${(props) =>
    props.isActive &&
    css`
      outline: 0.08rem solid ${({ theme }) => theme.colors.black};
    `}

  ${(props) =>
    props.typeDropDown === 'Table' &&
    css`
      border: none;
      outline: none;
      background: initial;
    `};

  ${(props) =>
    props.isDisabled &&
    css`
      background: ${({ theme }) => theme.colors.greyLight};
      border: none;
      cursor: not-allowed;
    `};

  ${(props) =>
    props.borderError &&
    css`
      border: 0.1rem solid ${({ theme }) => theme.colors.lightCoralRed};
    `};

  &:focus-within {
    outline: 0.08rem solid ${({ theme }) => theme.colors.black};
  }
`;

type SearchInputProps = {
  hasSelectedValue?: boolean;
};

export const SearchInput = styled.input<SearchInputProps>`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: ${({ theme }) => theme.fontSize.S};
  font-family: 'Poppins', sans-serif;
  color: ${({ theme }) => theme.colors.greyVeryDark};
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &::placeholder {
    /* Si hay selección, placeholder en negro (simula valor real) */
    /* Si NO hay selección, placeholder en gris */
    color: ${({ theme, hasSelectedValue }) =>
      hasSelectedValue ? theme.colors.greyVeryDark : theme.colors.greySoftLight};
    font-weight: ${({ theme }) => theme.fontWeight.regular};
  }

  &:focus {
    cursor: text;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.greyDark};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.XS};
  }
`;

export const ArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.4rem;
  margin: 0;
  flex-shrink: 0;
`;

export const ArrowDown = styled(ArrowBottom)`
  path {
    fill: ${({ theme }) => theme.colors.black};
  }
  vertical-align: middle;
  min-width: 1.3rem;
  min-height: 0.9rem;
  flex-shrink: 0;
`;

// List styles - siguiendo el patrón de DropDownList
export const DropDownListContainer = styled.div`
  z-index: 999;
  position: relative;
`;

type ListProps = {
  typeDropDown: TypeDropDown;
};

export const List = styled.ul<ListProps>`
  position: absolute;
  border-radius: 0.5rem;
  top: ${({ theme }) => theme.spacing.xxxs};
  color: ${({ theme }) => theme.colors.greyVeryDark};
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.dropdown};
  min-width: 100%;
  width: auto;
  max-height: 35rem;
  overflow: auto;
  
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.smallDesktop * theme.percentBaseResponsive}rem) {
    max-height: 18rem;
  }
  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    max-height: 17rem;
  }
`;

type ListItemProps = {
  isActive: boolean;
  type: TypeDropDown;
};

export const ListItem = styled.li<ListItemProps>`
  display: flex;
  align-items: center;
  list-style: none;
  font-size: ${({ theme }) => theme.fontSize.S};
  padding: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  
  ${(props) =>
    props.isActive &&
    css`
      background-color: ${props.theme.colors.backgroundLila};
      font-weight: ${props.theme.fontWeight.medium};
    `};
  
  /* Solo con puntero fino: en táctil el :hover se quedaría pegado tras el
     toque y sería indistinguible de la opción activa (ver styles/hover.ts). */
  ${hoverCapable} {
    &:hover {
      background-color: ${({ theme }) => theme.colors.backgroundLila};
      font-weight: ${({ theme }) => theme.fontWeight.medium};
    }
  }

  /* :focus-visible en vez de :focus — el resaltado se reserva a la navegación
     por teclado; al tocar, el foco se quedaba pintado como si fuera la opción
     elegida. */
  &:focus {
    outline: none;
  }

  &:focus-visible {
    background-color: ${({ theme }) => theme.colors.backgroundLila};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    outline: none;
  }

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.XS};
  }
`;

export const NoResultsText = styled.div`
  padding: 1rem;
  font-size: ${({ theme }) => theme.fontSize.S};
  color: ${({ theme }) => theme.colors.greyDark};
  font-style: italic;
  text-align: center;

  @media (max-width: ${({ theme }) =>
      theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.XS};
  }
`;

export const ErrorMessage = styled(Typography)``;

